/**
 * Sentry — error tracking e observabilidade do frontend.
 *
 * Configuração:
 *  - REACT_APP_SENTRY_DSN: DSN do projeto (obtido em sentry.io)
 *  - REACT_APP_SENTRY_ENV: production/staging/dev (default: production)
 *  - REACT_APP_VERSION: SHA do commit (já gerado por scripts/gerar-version.js)
 *
 * Sem DSN configurada, o módulo é silencioso (no-op) — útil em dev local.
 *
 * Sample rates:
 *  - tracesSampleRate 0.1 = 10% das transações capturadas (controla custo)
 *  - replaysSessionSampleRate 0 = sem session replay em sessões normais
 *  - replaysOnErrorSampleRate 1.0 = 100% replay quando houver erro
 *
 * Privacidade (LGPD):
 *  - maskAllText/maskAllInputs no replay pra não vazar dados de usuário
 *  - beforeSend filtra erros conhecidos não-acionáveis (ex.: ResizeObserver)
 */
import * as Sentry from '@sentry/react';

const DSN = process.env.REACT_APP_SENTRY_DSN;
const RELEASE = process.env.REACT_APP_VERSION || 'dev-local';

// O environment veio com o DSN dentro (o Sentry recusou: "Discarded invalid
// value / Name: environment"). Alguém colou a DSN nas duas variáveis no
// Vercel. Sem a guarda, TODO evento chega sem ambiente e não dá pra separar
// produção de teste no painel.
const _envBruto = process.env.REACT_APP_SENTRY_ENV || process.env.NODE_ENV || 'production';
const ENV = (/^https?:\/\//i.test(_envBruto) || _envBruto.length > 40)
  ? 'production'
  : _envBruto;

const ERROS_IGNORADOS = [
  /ResizeObserver loop/i,
  /Non-Error promise rejection captured/i,
  /Network request failed/i,
  /Failed to fetch/i,
  /Load failed/i,
  /AbortError/i,

  // ── Navegadores IN-APP (Instagram, Facebook, TikTok…) ────────────────
  // Essas redes injetam o próprio script de rastreamento na página e ele
  // quebra sozinho no WebView do iOS. `sendDataToNative`/`sendPageHideMessage`
  // e `window.webkit.messageHandlers` são código da Meta, não nosso — o
  // stack trace aponta pro documento HTML porque o script é injetado em
  // runtime. Não há nada a corrigir do nosso lado, e o usuário não vê nada.
  // Importa filtrar porque divulgação no Instagram traz MUITO tráfego pelo
  // in-app browser: sem isso o monitor vira ruído e esconde erro de verdade.
  /window\.webkit\.messageHandlers/i,
  /sendDataToNative/i,
  /sendPageHideMessage/i,
  /webkit\.messageHandlers/i,
];

// Navegador embutido de rede social (o User-Agent traz o nome do app)
const _EH_INAPP = /Instagram|FBAN|FBAV|FB_IAB|Line\/|TikTok|Twitter|Snapchat/i;

export function initSentry() {
  if (!DSN) return;
  Sentry.init({
    dsn: DSN,
    environment: ENV,
    release: RELEASE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, maskAllInputs: true, blockAllMedia: true }),
    ],
    tracesSampleRate: ENV === 'production' ? 0.1 : 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: false,
    beforeSend(event, hint) {
      const msg = hint?.originalException?.message || event.message || '';
      if (ERROS_IGNORADOS.some(r => r.test(msg))) return null;

      // Erro sem NENHUM frame do nosso bundle, dentro de navegador in-app:
      // é script injetado pela rede social. O filtro por mensagem acima pega
      // os casos conhecidos; este pega os que a Meta inventar amanhã.
      try {
        if (_EH_INAPP.test(navigator.userAgent || '')) {
          const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];
          const temNosso = frames.some(f => /\/static\/(js|css)\//.test(f.filename || ''));
          if (frames.length > 0 && !temNosso) return null;
        }
      } catch (e) { /* na dúvida, deixa passar — perder erro é pior */ }

      return event;
    },
  });
}

export function identifySentryUser(usuario) {
  if (!DSN || !usuario) return;
  Sentry.setUser({
    id: usuario.id,
    perfil_ativo: usuario.perfil_ativo,
    nivel: usuario.nivel,
  });
}

export function clearSentryUser() {
  if (!DSN) return;
  Sentry.setUser(null);
}

export { Sentry };
